<template>
  <el-dialog
    title="发起评估"
    :visible.sync="innerVisible"
    width="720px"
    @open="loadOptions"
    @closed="resetForm"
  >
    <el-form :model="form" label-width="110px" size="small" v-loading="loading">
      <el-form-item label="任务名" required>
        <el-input v-model="form.run_name" placeholder="如 eval-0920-a" clearable />
        <div class="form-hint">
          只能用字母数字与 _ . -（以字母数字开头，不超过 64 位）；不能重名
        </div>
      </el-form-item>

      <el-form-item label="数据集" required>
        <el-select
          v-model="form.dataset_ref"
          placeholder="请选择数据集版本"
          style="width:100%"
          @change="handleDatasetChange"
        >
          <el-option
            v-for="item in datasets"
            :key="item.ref"
            :label="datasetLabel(item)"
            :value="item.ref"
          >
            <span class="option-row">
              <span class="option-main">{{ datasetLabel(item) }}</span>
              <el-button
                v-if="item.cache_state === 'missing'"
                type="text"
                size="mini"
                :disabled="pullingRefs.includes(item.ref)"
                @click.stop="handleMaterialize(item)"
              >下载到本地</el-button>
              <span v-else-if="item.cache_state === 'pulling'" class="option-side">
                {{ pullPercent(item) }}
              </span>
            </span>
          </el-option>
        </el-select>
        <div v-if="!loading && !datasets.length" class="form-hint warn">
          还没有数据集版本，先去「数据集」页发布一份
        </div>
      </el-form-item>

      <el-form-item label="划分" required>
        <el-select
          v-model="form.split"
          placeholder="请选择划分"
          style="width:100%"
          :disabled="!selectedDataset"
        >
          <el-option
            v-for="item in splitOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
        <div v-if="!loading && selectedDataset && !splitOptions.length" class="form-hint warn">
          这一版 train / val 都是空的，没有可评估的划分
        </div>
      </el-form-item>

      <el-form-item label="被评模型" required>
        <el-select v-model="form.model_id" placeholder="请选择模型" style="width:100%">
          <el-option
            v-for="item in models"
            :key="item.model_id"
            :label="modelLabel(item)"
            :value="item.model_id"
          />
        </el-select>
        <div v-if="!loading && !models.length" class="form-hint warn">
          还没有模型，先去「模型实例」页上传一个 .pt，或从 MinIO 导入
        </div>
      </el-form-item>

      <el-form-item label="评估方案" required>
        <el-select v-model="form.profile" style="width:100%">
          <el-option label="默认方案（default）" value="default" />
        </el-select>
        <div class="form-hint">
          覆盖率阈值 0.85、置信度阈值 0.25、letterbox 预处理；类别按名称映射 dynamic / static
        </div>
      </el-form-item>

      <el-form-item label="使用显卡" required>
        <el-select
          v-model="form.gpu_ids"
          multiple
          placeholder="请选择显卡"
          style="width:100%"
        >
          <el-option
            v-for="card in gpus"
            :key="card.index"
            :label="gpuLabel(card)"
            :value="card.index"
            :disabled="Boolean(card.occupied_by)"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="imgsz">
        <el-input-number v-model="form.imgsz" :min="1" :max="4096" :step="32" controls-position="right" />
      </el-form-item>
    </el-form>

    <div slot="footer" class="dialog-footer">
      <div v-if="notReadyHint" class="footer-warn">{{ notReadyHint }}</div>
      <el-button size="small" @click="innerVisible = false">取消</el-button>
      <el-button
        type="primary"
        size="small"
        :loading="dispatching"
        :disabled="!canDispatch"
        @click="handleDispatch"
      >发起评估</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { fetchTrainDatasets, fetchTrainGpus, materializeDataset } from '../api/blissrose/training';
import { dispatchEval, fetchEvalModels } from '../api/blissrose/evaluation';

const DEFAULT_FORM = {
  run_name: '',
  dataset_ref: '',
  split: '',
  model_id: '',
  profile: 'default',
  gpu_ids: [],
  imgsz: 640,
};

// 下载进度轮询间隔；一次只问一版清单
const PULL_POLL_MS = 2000;
// 连续问不通几次就停，免得提示刷满页面
const PULL_POLL_MAX_FAILURES = 3;

// 模型来源文案
const SOURCE_LABELS = { upload: '上传', minio: 'MinIO 导入' };

/** 发起评估对话框：数据集与显卡清单复用训练，被评模型含训练产物；划分必选不预选，未就绪版本仍可选、能不能评由执行器判 */
export default {
  name: 'EvaluationDispatchDialog',

  props: {
    visible: { type: Boolean, default: false },
  },

  data() {
    return {
      loading: false,
      dispatching: false,
      datasets: [],
      models: [],
      gpus: [],
      // 正在等落地的那几版
      pullingRefs: [],
      pollTimer: null,
      pollFailures: 0,
      form: { ...DEFAULT_FORM },
    };
  },

  computed: {
    innerVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      },
    },

    canDispatch() {
      const { run_name: runName, dataset_ref: ref, split, model_id: modelId, gpu_ids: gpuIds } = this.form;
      return Boolean(runName && ref && split && modelId && gpuIds.length);
    },

    selectedDataset() {
      return this.datasets.find(item => item.ref === this.form.dataset_ref) || null;
    },

    /** 划分下拉选项：有帧数的划分才在 */
    splitOptions() {
      const item = this.selectedDataset;
      if (!item) return [];
      const options = [];
      if (item.train_count > 0) options.push({ value: 'train', label: `train（${item.train_count} 帧）` });
      if (item.val_count > 0) options.push({ value: 'val', label: `val（${item.val_count} 帧）` });
      if (item.test_count > 0) options.push({ value: 'test', label: `test（${item.test_count} 帧）` });
      return options;
    },

    /** 选中版本未就绪时的提醒，贴在派发按钮旁；不禁用按钮，就绪判定只在执行器一处 */
    notReadyHint() {
      const item = this.selectedDataset;
      if (!item || item.cache_state === 'ready') return '';
      if (item.cache_state === 'pulling') {
        const progress = this.pullPercent(item);
        return `这一版还在下载到训练机的路上${progress ? `（${progress}）` : ''}，下载完才能评估`;
      }
      if (item.cache_state === 'missing') {
        const base = '这一版还没下载到本地，先点它那行的「下载到本地」';
        return item.cache_error ? `${base}（上次下载失败：${item.cache_error}）` : base;
      }
      return '本地状态读不出来，稍后重试或先下载一次';
    },
  },

  beforeDestroy() {
    this.stopPolling();
  },

  methods: {
    /** 一次性拉三个下拉的数据源；某个拉不到时只让那一个下拉为空，不挡其余 */
    async loadOptions() {
      this.loading = true;
      await Promise.all([this.loadDatasets(), this.loadModels(), this.loadGpus()]);
      this.loading = false;
      this.form.run_name = this.suggestRunName();
      // 打开时已在下载的版本接着往下看
      this.pullingRefs = this.datasets
        .filter(item => item.cache_state === 'pulling')
        .map(item => item.ref);
      if (this.pullingRefs.length) this.startPolling();
    },

    async loadDatasets() {
      try {
        const { items } = (await fetchTrainDatasets()).data;
        this.datasets = items || [];
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      }
    },

    async loadModels() {
      try {
        const { items } = (await fetchEvalModels()).data;
        this.models = items || [];
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      }
    },

    async loadGpus() {
      try {
        const { items } = (await fetchTrainGpus()).data;
        this.gpus = items || [];
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      }
    },

    /** 换数据集就清掉划分 */
    handleDatasetChange() {
      this.form.split = '';
    },

    resetForm() {
      this.stopPolling();
      this.pullingRefs = [];
      this.form = { ...DEFAULT_FORM };
      this.datasets = [];
      this.models = [];
      this.gpus = [];
    },

    /** 任务名种子：eval 前缀加日期时分 */
    suggestRunName() {
      const now = new Date();
      const pad = n => String(n).padStart(2, '0');
      return `eval-${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
    },

    async handleDispatch() {
      this.dispatching = true;
      try {
        const { warnings } = (await dispatchEval({ ...this.form })).data;
        this.$message.success('评估已派发');
        this.innerVisible = false;
        this.$emit('dispatched');
        // 警告不阻止派发，只随提示给出
        if (warnings && warnings.length) {
          this.$notify({
            title: '派发已提交，但有几点需要注意',
            message: warnings.join('；'),
            type: 'warning',
            duration: 0,
          });
        }
      } catch (err) {
        // 拒绝原因由响应拦截器弹出
      } finally {
        this.dispatching = false;
      }
    },

    // ====== 下载到本地 ======

    /** 下载该版本到训练机本地并跟踪落地状态；平台立刻返回，此处只记入轮询名单 */
    async handleMaterialize(item) {
      this.startPolling();
      if (!this.pullingRefs.includes(item.ref)) this.pullingRefs.push(item.ref);
      try {
        const { state } = (await materializeDataset(item.ref)).data;
        if (state === 'ready') {
          this.$message.success(`${item.ref} 训练机上已经有了，不必再下载`);
          this.dropPulled(item.ref);
          await this.loadDatasets();
          return;
        }
        this.$message.success(`已开始下载 ${item.ref}，进度见列表（完成前该行标「下载中」）`);
      } catch (err) {
        // 拒绝原因由拦截器弹出；该版本移出轮询名单
        this.pullingRefs = this.pullingRefs.filter(ref => ref !== item.ref);
        if (!this.pullingRefs.length) this.stopPolling();
      }
    },

    startPolling() {
      if (this.pollTimer) return;
      this.pollFailures = 0;
      this.pollTimer = setInterval(this.pollOnce, PULL_POLL_MS);
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
      this.pollFailures = 0;
    },

    /** 一轮轮询：刷新清单，把已落地或已失败的版本摘出名单；直接发请求，这里要能数得出问不通 */
    async pollOnce() {
      if (!this.pullingRefs.length) {
        this.stopPolling();
        return;
      }
      let items;
      try {
        items = (await fetchTrainDatasets()).data.items || [];
        this.pollFailures = 0;
      } catch (err) {
        this.pollFailures += 1;
        if (this.pollFailures >= PULL_POLL_MAX_FAILURES) this.stopPolling();
        return;
      }
      this.datasets = items;
      const byRef = new Map(items.map(item => [item.ref, item]));
      const pending = [];
      for (const ref of this.pullingRefs) {
        const item = byRef.get(ref);
        if (!item) continue; // 清单里已无该版本
        if (item.cache_state === 'ready') {
          this.$message.success(`${ref} 已下载到训练机本地`);
          continue;
        }
        if (item.cache_state === 'pulling') {
          pending.push(ref);
          continue;
        }
        // 既没就绪又没在下载：这一趟没成
        this.$notify({
          title: '下载没成',
          message: `${ref}：${item.cache_error || '平台没记下原因'}`,
          type: 'error',
          duration: 0,
        });
      }
      this.pullingRefs = pending;
      if (!pending.length) this.stopPolling();
    },

    /** 把该版本移出轮询名单：平台响应已当场确认，无须再由轮询复核 */
    dropPulled(ref) {
      this.pullingRefs = this.pullingRefs.filter(item => item !== ref);
      if (!this.pullingRefs.length) this.stopPolling();
    },

    // ====== 展示辅助 ======

    /** 下拉一行：名@版本 + 划分帧数 + 类别数 + 本地状态；一律说「帧」不说「张图」 */
    datasetLabel(item) {
      const parts = [];
      if (item.train_count || item.val_count || item.test_count) {
        parts.push(`train ${item.train_count} 帧`, `val ${item.val_count} 帧`);
        if (item.test_count) parts.push(`test ${item.test_count} 帧`);
      } else if (item.frame_count) {
        parts.push(`${item.frame_count} 帧`);
      }
      if (item.class_count) parts.push(`${item.class_count} 类`);
      // 就绪不挂字，未就绪才说话（见 notReadyHint）
      const marks = { ready: '', missing: ' · 未下载到本地', pulling: ' · 下载中' };
      const mark = item.cache_state in marks ? marks[item.cache_state] : ' · 状态未知';
      const info = parts.length ? `（${parts.join('，')}）` : '';
      return `${item.ref}${info}${mark}`;
    },

    /** 下拉一行：模型名 + 来源 */
    modelLabel(item) {
      const source = SOURCE_LABELS[item.source] || item.source || '';
      return source ? `${item.name}（${source}）` : item.name;
    },

    /** 已下载多少；总数没报出前不显示百分比 */
    pullPercent(item) {
      const total = item.cache_total || 0;
      if (!total) return '';
      return `${Math.floor(((item.cache_done || 0) / total) * 100)}%`;
    },

    /** 显存与占用任务一并显示 */
    gpuLabel(card) {
      const parts = [`卡 ${card.index}`];
      if (card.name) parts.push(card.name);
      if (card.memory_used !== null && card.memory_used !== undefined && card.memory_total) {
        parts.push(`${card.memory_used}/${card.memory_total} MiB`);
      }
      if (card.occupied_by) parts.push(`被 ${card.occupied_by} 占用`);
      return parts.join(' · ');
    },
  },
};
</script>

<style scoped>
.form-hint {
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
  margin-top: 4px;
}

.form-hint.warn {
  color: #e6a23c;
}

.dialog-footer {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dialog-footer .footer-warn {
  flex: 1;
  text-align: left;
  color: #e6a23c;
  font-size: 12px;
  line-height: 1.5;
}

.option-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.option-row .option-main {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.option-row .option-side {
  color: #909399;
  font-size: 12px;
}
</style>
